/**
 * Document ordering for Structure (Act / Scene / Song script markers) against cues.
 *
 * Pure and framework-agnostic on purpose — no Vue, no Vuex, no DOM — like
 * `listViewCellNav.js` and `eosExport.js`. It survives the Vue 3 migration and is
 * unit-testable in isolation (test/unit/specs/structureOrder.spec.js).
 *
 * THIS IS THE CANONICAL CROSS-PAGE COMPARATOR. The four page-local cue comparators
 * (`project/page.vue`, `print/print-page.vue`, `project/thumbnail-page.vue`,
 * `project/pages.vue`) each compare y then x with NO page term, and each returns
 * 0/1 or a raw boolean instead of a tri-state. They are legacy and deliberately
 * left alone: their observed output is baked into on-page z-order and print
 * layout, so fixing them is a separate change with its own QA surface.
 *
 * Markers key off `pageIndex`, cues off `pageNumber`. Both are 1-based and share
 * the same numbering space.
 */

/**
 * Legacy docs can carry raw PIXEL x/y instead of the 0..1 fractions written today
 * (see the `<= 1` guards in cue-position-marker.vue, script-marker.vue, and the
 * re-normalisation in page.vue). A comparator that ignores this sinks every pixel
 * value below every fraction, which would let one stale marker swallow a whole
 * section.
 *
 * We divide by a CONSTANT rather than the real page height because the real page
 * height is not available here: in full list view no PDF page is mounted, so
 * `updatePageSize` never fires and `project.pageSize` is `{}`. Reading render
 * state from a pure comparator is the coupling that makes the column-width engine
 * fragile; do not repeat it. Legacy values were authored against pages rendered
 * at roughly 600-1100px, so /1000 lands them in the same 0..1 band.
 *
 * This makes ordering APPROXIMATELY right instead of CATASTROPHICALLY wrong. The
 * real fix is a one-time normalisation backfill of `cues` and `scriptMarkers`.
 */
export const LEGACY_COORD_UNIT = 1000;

/** Act is the broadest division, song the narrowest. Also the render order. */
export const MARKER_TYPE_RANK = { act: 0, scene: 1, song: 2 };

/** Type colours, shared with the on-page marker so the two cannot drift. */
export const MARKER_TYPE_COLORS = {
    act: { bg: '#5A7298', fg: '#ffffff' },
    scene: { bg: '#6589AB', fg: '#ffffff' },
    song: { bg: '#CAD5E1', fg: '#2c3e50' }
};

let warnedLegacyCoords = false;

/** Normalise an x/y that may be a 0..1 fraction or a legacy raw pixel value. */
export function normCoord(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return 0;
    }
    if (n <= 1) {
        return n;
    }
    if (!warnedLegacyCoords && typeof console !== 'undefined' && console.warn) {
        warnedLegacyCoords = true;
        console.warn('[structureOrder] legacy pixel coordinate seen (' + n + '); section order is approximate for this project. A normalisation backfill is needed.');
    }
    return Math.min(1, n / LEGACY_COORD_UNIT);
}

/** Reset the one-shot legacy-coordinate warning. Test seam only. */
export function resetLegacyCoordWarning() {
    warnedLegacyCoords = false;
}

/** Positional key for a marker or a cue: { page, y, x }. */
export function docKey(item) {
    if (!item) {
        return { page: 0, y: 0, x: 0 };
    }
    const rawPage = item.pageIndex === undefined ? item.pageNumber : item.pageIndex;
    const page = Number(rawPage);
    return {
        page: Number.isFinite(page) ? page : 0,
        y: normCoord(item.y),
        x: normCoord(item.x)
    };
}

function markerId(marker) {
    if (!marker) {
        return '';
    }
    return String(marker.markerId || marker.id || '');
}

/**
 * Tri-state document-order comparator over { item, isMarker } entries.
 *
 * At an identical (page, y, x) the MARKER sorts first, so a cue sitting exactly on
 * a marker falls INSIDE that marker's section with no special-casing downstream.
 * Two markers at one point order act -> scene -> song, then by id for stability.
 */
export function compareDocOrder(a, b) {
    const ka = docKey(a.item);
    const kb = docKey(b.item);
    if (ka.page !== kb.page) {
        return ka.page < kb.page ? -1 : 1;
    }
    if (ka.y !== kb.y) {
        return ka.y < kb.y ? -1 : 1;
    }
    if (ka.x !== kb.x) {
        return ka.x < kb.x ? -1 : 1;
    }
    if (a.isMarker !== b.isMarker) {
        return a.isMarker ? -1 : 1;
    }
    if (a.isMarker && b.isMarker) {
        const ra = MARKER_TYPE_RANK[a.item.type] === undefined ? 1 : MARKER_TYPE_RANK[a.item.type];
        const rb = MARKER_TYPE_RANK[b.item.type] === undefined ? 1 : MARKER_TYPE_RANK[b.item.type];
        if (ra !== rb) {
            return ra < rb ? -1 : 1;
        }
        const ia = markerId(a.item);
        const ib = markerId(b.item);
        if (ia !== ib) {
            return ia < ib ? -1 : 1;
        }
    }
    return 0;
}

/**
 * Reduce the run of markers sitting between the previous cue and this one to the
 * chain that actually applies to this cue.
 *
 * Walking backwards, keep a marker only if it is BROADER than everything kept so
 * far. The last marker in the run owns the cue; a preceding Act is its parent and
 * is kept; a preceding Scene that owns no visible cue of its own is dropped.
 *
 *   [Act 2, Scene 1]  -> both      (act boundary: show "ACT TWO" then "Scene 1")
 *   [Scene 2, Scene 3] -> Scene 3  (Scene 2's cues are filtered out or it is empty)
 *   [Act 2, Scene 1, Song A] -> all three
 *
 * This is also what elides empty sections: a marker only ever renders directly
 * above a cue it applies to, so a section with no visible cues never appears.
 */
function applicableChain(run) {
    const kept = [];
    let minRank = Number.POSITIVE_INFINITY;
    for (let i = run.length - 1; i >= 0; i -= 1) {
        const marker = run[i];
        const rank = MARKER_TYPE_RANK[marker.type] === undefined ? 1 : MARKER_TYPE_RANK[marker.type];
        if (kept.length === 0 || rank < minRank) {
            kept.push(marker);
            minRank = rank;
        }
    }
    kept.reverse();
    return kept;
}

/**
 * Work out which markers head which cues, purely from document position.
 *
 * Deliberately independent of the caller's display order: it sorts its own copy,
 * so headers attach to the right cue whatever order the list is showing.
 *
 * Returns:
 *   headersByCueId  { [cueId]: marker[] } — markers to render above that cue
 *   orderedCues     the cues in true document order
 */
export function assignSections(cues, markers) {
    const cueList = Array.isArray(cues) ? cues.filter(Boolean) : [];
    const markerList = Array.isArray(markers) ? markers.filter(Boolean) : [];
    const headersByCueId = {};
    const orderedCues = cueList
        .map(cue => ({ item: cue, isMarker: false }))
        .sort(compareDocOrder)
        .map(entry => entry.item);

    if (markerList.length === 0) {
        return { headersByCueId: headersByCueId, orderedCues: orderedCues };
    }

    const merged = cueList
        .map(cue => ({ item: cue, isMarker: false }))
        .concat(markerList.map(marker => ({ item: marker, isMarker: true })))
        .sort(compareDocOrder);

    let run = [];
    for (let i = 0; i < merged.length; i += 1) {
        const entry = merged[i];
        if (entry.isMarker) {
            run.push(entry.item);
        } else if (run.length > 0) {
            headersByCueId[entry.item.id] = applicableChain(run);
            run = [];
        }
    }
    // Any markers trailing the last cue own nothing visible and are dropped.

    return { headersByCueId: headersByCueId, orderedCues: orderedCues };
}

function headerRow(marker) {
    return { kind: 'header', marker: marker, key: 'h:' + markerId(marker), isEmpty: false, cueCount: 0 };
}

function rankOf(marker) {
    const rank = MARKER_TYPE_RANK[marker && marker.type];
    return rank === undefined ? 1 : rank;
}

/**
 * Count the cues each header owns, and flag headers that own none.
 *
 * "Owns no cue" is NOT simply "the next row is another header": an Act sitting
 * directly above Scene 1 owns every cue in that scene. A marker is empty only
 * when no cue appears before the next marker of the SAME OR BROADER rank — so an
 * Act ends at the next Act, a Scene at the next Scene or Act, and a Song at the
 * next anything.
 *
 * One backward pass: a cue adds one to every open window, and a marker of
 * rank r reads its window, then closes it for every rank at or narrower than r.
 * The count is of the rows given, i.e. the cues List view is showing.
 */
function markEmptyHeaders(rows) {
    const count = [0, 0, 0];
    for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (rows[i].kind === 'cue') {
            count[0] += 1;
            count[1] += 1;
            count[2] += 1;
        } else {
            const rank = rankOf(rows[i].marker);
            rows[i].cueCount = count[rank];
            rows[i].isEmpty = count[rank] === 0;
            for (let r = rank; r < count.length; r += 1) {
                count[r] = 0;
            }
        }
    }
}

/**
 * Interleave header rows into a DISPLAY-ordered cue list.
 *
 * EVERY marker renders, at the point it falls in document order, whether or not
 * any cue follows it. A section with no cues is information, not noise: "this
 * scene has nothing in the layer I am looking at" is exactly what a designer
 * wants to notice, and hiding it means a project whose markers all sit past the
 * last cue shows no structure at all — with no way to tell that any exists.
 * Markers trailing every cue therefore stack at the bottom of the list.
 *
 * `isEmpty` marks a header with no cue of its own (the next row is another
 * header, or there is no next row) so the view can label it.
 *
 * `cueIndex` is the index into the caller's own cue array, so downstream
 * coordinates (list view's `data-ql-r`) stay keyed to cues, not to rows.
 *
 * Assumes `displayCues` is already in document order, which is the only
 * situation the list view renders headers in.
 *
 *   [ { kind: 'header', marker, key, isEmpty } | { kind: 'cue', cue, cueIndex } ]
 */
export function buildStructureRows(displayCues, markers) {
    const cues = Array.isArray(displayCues) ? displayCues : [];
    const markerList = Array.isArray(markers) ? markers.filter(Boolean) : [];
    if (markerList.length === 0) {
        return cues.map((cue, i) => ({ kind: 'cue', cue: cue, cueIndex: i }));
    }

    const ordered = markerList
        .map(marker => ({ item: marker, isMarker: true }))
        .sort(compareDocOrder);

    const rows = [];
    let mi = 0;
    for (let i = 0; i < cues.length; i += 1) {
        const cueEntry = { item: cues[i], isMarker: false };
        while (mi < ordered.length && compareDocOrder(ordered[mi], cueEntry) < 0) {
            rows.push(headerRow(ordered[mi].item));
            mi += 1;
        }
        rows.push({ kind: 'cue', cue: cues[i], cueIndex: i });
    }
    while (mi < ordered.length) {
        rows.push(headerRow(ordered[mi].item));
        mi += 1;
    }

    markEmptyHeaders(rows);
    return rows;
}

/**
 * The label for a field that holds exactly one: the INNERMOST marker heading the
 * cue, verbatim.
 *
 * An earlier version combined the chain ("Act 1 - Prologue") on the theory that it
 * was lossless. It is not what an operator wants. A combined label reads as the
 * name of a scene that does not exist, and because the collision only happens at
 * act boundaries the export comes out inconsistent — the first scene of each act
 * carries an act prefix and every other scene does not. One marker, one label,
 * every row the same shape.
 */
export function innermostSceneLabel(chain) {
    const list = Array.isArray(chain) ? chain : [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
        const label = String((list[i] && list[i].label) || '').trim();
        if (label) {
            return label;
        }
    }
    return '';
}

/** Keep only the marker types in `types` (default: all three). */
export function filterMarkerTypes(markers, types) {
    const list = Array.isArray(markers) ? markers : [];
    if (!Array.isArray(types)) {
        return list;
    }
    return list.filter(marker => types.indexOf(marker && marker.type) !== -1);
}

/** Page name for a marker's 1-based pageIndex, matching the sidebar's rule. */
export function pdfPageLabel(pdfPageNumbers, pageIndex) {
    const idx = Number(pageIndex);
    if (!Number.isFinite(idx)) {
        return '';
    }
    const list = Array.isArray(pdfPageNumbers) ? pdfPageNumbers : [];
    const entry = (idx - 1) >= 0 && (idx - 1) < list.length ? list[idx - 1] : null;
    if (entry && (entry.name || entry.pageNumber)) {
        return String(entry.name || entry.pageNumber);
    }
    return String(idx);
}

/**
 * The two cells a CSV export writes for a section header: the name with its cue
 * count ("Scene 6 Camp HB (1 cue)"), and the page the section starts on.
 *
 * Reported 11 Oct 2026: the section name sat under "Page Number" with no page, and
 * only empty sections said anything about their cues ("(no cues)"). The export now
 * gives sections their own first column and a real page (Josh, 12 Oct).
 */
export function sectionCellsForExport(label, cueCount, pdfPageNumbers, marker) {
    const n = Number(cueCount) || 0;
    const name = String(label || '').trim();
    const section = name + ' (' + n + (n === 1 ? ' cue)' : ' cues)');
    const rawPage = marker && marker.pageIndex !== undefined ? marker.pageIndex : (marker && marker.pageNumber);
    const page = rawPage === undefined || rawPage === null || rawPage === '' ? '' : pdfPageLabel(pdfPageNumbers, rawPage);
    return { section, page };
}


// WEBPACK FOOTER //
// ./src/lib/structureOrder.js